import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

// ============== URL CONFIGURATION ==============
// Priority: ENV var > window.location.origin fallback
const getBaseUrl = () => {
  // First try the environment variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback to current origin (works in production)
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }
  // Last resort - should never happen in browser
  console.error('ScanSavvy: No base URL available - QR codes will not work correctly');
  return '';
};

const BACKEND_URL = getBaseUrl();
const API = `${BACKEND_URL}/api`;

// ============== LOCAL STORAGE HELPERS ==============
const STORAGE_KEYS = {
  SELECTED_STORES: 'scansavvy_selected_stores',
  CURRENT_BUNDLE: 'scansavvy_current_bundle',
  USER_ID: 'scansavvy_user_id'
};

// ============== CLEAN COUPON DATA (MVP) ==============
// Small, realistic dataset - no duplicates, checkout-ready
const STORE_COUPONS = {
  walmart: [
    { id: "wm-1", title: "$5 OFF $50 Grocery", discount: "$5 OFF", description: "Any grocery purchase $50+", expiry: "Mar 23, 2026" },
    { id: "wm-2", title: "20% OFF Household", discount: "20% OFF", description: "Cleaning supplies & paper goods", expiry: "Mar 23, 2026" },
    { id: "wm-3", title: "$3 OFF Tide Detergent", discount: "$3 OFF", description: "Tide 92oz or larger", expiry: "Mar 23, 2026" },
    { id: "wm-4", title: "BOGO Snacks", discount: "BOGO 50%", description: "Select chips & snacks", expiry: "Mar 23, 2026" },
  ],
  target: [
    { id: "tgt-1", title: "25% OFF Home Decor", discount: "25% OFF", description: "Select home decor items", expiry: "Mar 23, 2026" },
    { id: "tgt-2", title: "$5 OFF $25 Beauty", discount: "$5 OFF", description: "Beauty products $25+", expiry: "Mar 23, 2026" },
    { id: "tgt-3", title: "Buy 2 Get 1 Free Books", discount: "B2G1", description: "Select book titles", expiry: "Mar 23, 2026" },
    { id: "tgt-4", title: "20% OFF Clothing", discount: "20% OFF", description: "Apparel & accessories", expiry: "Mar 23, 2026" },
  ],
  kroger: [
    { id: "kr-1", title: "$1 OFF Kroger Brand", discount: "$1 OFF", description: "Any 3 Kroger products", expiry: "Mar 23, 2026" },
    { id: "kr-2", title: "BOGO Cereal", discount: "BOGO", description: "Select cereals", expiry: "Mar 23, 2026" },
    { id: "kr-3", title: "$2 OFF Fresh Meat", discount: "$2 OFF", description: "Beef, chicken, or pork", expiry: "Mar 23, 2026" },
    { id: "kr-4", title: "30% OFF Produce", discount: "30% OFF", description: "Fresh fruits & vegetables", expiry: "Mar 23, 2026" },
  ],
  cvs: [
    { id: "cvs-1", title: "40% OFF Vitamins", discount: "40% OFF", description: "All vitamins & supplements", expiry: "Mar 23, 2026" },
    { id: "cvs-2", title: "$5 ExtraBucks", discount: "$5 BACK", description: "Spend $20 or more", expiry: "Mar 23, 2026" },
    { id: "cvs-3", title: "BOGO Skincare", discount: "BOGO 50%", description: "Select skincare products", expiry: "Mar 23, 2026" },
  ],
  walgreens: [
    { id: "wg-1", title: "$2 OFF Walgreens Brand", discount: "$2 OFF", description: "Any Walgreens product", expiry: "Mar 23, 2026" },
    { id: "wg-2", title: "30% OFF Cosmetics", discount: "30% OFF", description: "Select cosmetics & beauty", expiry: "Mar 23, 2026" },
    { id: "wg-3", title: "BOGO Candy", discount: "BOGO", description: "Select candy items", expiry: "Mar 23, 2026" },
  ],
  costco: [
    { id: "cc-1", title: "$10 OFF $100", discount: "$10 OFF", description: "Orders $100 or more", expiry: "Mar 23, 2026" },
    { id: "cc-2", title: "$5 OFF Kirkland", discount: "$5 OFF", description: "Any 2 Kirkland items", expiry: "Mar 23, 2026" },
    { id: "cc-3", title: "15% OFF Electronics", discount: "15% OFF", description: "Select electronics", expiry: "Mar 23, 2026" },
  ],
  publix: [
    { id: "pb-1", title: "BOGO Ice Cream", discount: "BOGO", description: "Publix brand ice cream", expiry: "Mar 23, 2026" },
    { id: "pb-2", title: "$2 OFF Deli", discount: "$2 OFF", description: "Sliced deli meats", expiry: "Mar 23, 2026" },
    { id: "pb-3", title: "20% OFF Bakery", discount: "20% OFF", description: "Fresh bakery products", expiry: "Mar 23, 2026" },
  ],
  aldi: [
    { id: "al-1", title: "$3 OFF $30", discount: "$3 OFF", description: "Orders $30 or more", expiry: "Mar 23, 2026" },
    { id: "al-2", title: "20% OFF Produce", discount: "20% OFF", description: "All fruits & vegetables", expiry: "Mar 23, 2026" },
  ],
};

// Calculate savings from discount string
const parseSavings = (discount) => {
  if (discount.includes("$")) {
    const match = discount.match(/\$(\d+)/);
    return match ? parseFloat(match[1]) : 5;
  }
  if (discount.includes("%")) {
    const match = discount.match(/(\d+)%/);
    return match ? parseFloat(match[1]) * 0.2 : 4; // Estimate 20% of $20 avg item
  }
  if (discount.includes("BOGO")) return 5;
  return 3;
};

// Generate bundle from selected stores (consistent, not random)
// Create a real bundle via Supabase API
const createSupabaseBundle = async (selectedStores, userName = "Guest", userEmail = null) => {
  try {
    const response = await axios.post(`${API}/supabase/bundles/create`, {
      store_ids: selectedStores,
      user_name: userName,
      user_email: userEmail
    });
    
    if (response.data.success && response.data.bundle) {
      const bundleData = response.data.bundle;
      return {
        id: bundleData.id,
        week_label: bundleData.week_label,
        valid_until: bundleData.valid_until,
        total_savings: bundleData.total_savings,
        coupon_count: bundleData.coupon_count,
        coupons: bundleData.coupons || [],
        stores_included: bundleData.stores || selectedStores.map(id => ({ id, name: id })),
        bundle_type: 'weekly',
        qr_url: response.data.qr_url
      };
    }
    throw new Error("Failed to create bundle");
  } catch (error) {
    console.error("Supabase bundle creation failed:", error);
    // Fallback to local mock if API fails
    return generateLocalBundle(selectedStores);
  }
};

// Fallback mock bundle generator (only used if Supabase fails)
const generateLocalBundle = (selectedStores) => {
  const coupons = [];
  let totalSavings = 0;
  
  selectedStores.forEach(storeId => {
    const storeCoupons = STORE_COUPONS[storeId] || [];
    storeCoupons.forEach(coupon => {
      coupons.push({ ...coupon, storeId });
      totalSavings += parseSavings(coupon.discount);
    });
  });
  
  // Generate consistent bundle ID based on stores (not random)
  const bundleId = 'LOCAL-' + selectedStores.sort().join('-').slice(0, 20);
  
  return {
    id: bundleId,
    coupons,
    total_savings: totalSavings.toFixed(2),
    coupon_count: coupons.length,
    week_label: "This Week's Savings",
    valid_until: "March 23, 2026",
    stores_included: selectedStores.map(id => ({ id, name: id })),
    bundle_type: 'local'
  };
};

// Save to local storage
const saveToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Failed to save to localStorage', e);
  }
};

// Load from local storage
const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.error('Failed to load from localStorage', e);
    return defaultValue;
  }
};

// Production URL for QR codes (must be HTTPS and fully qualified)
const getQRCodeUrl = (bundleId) => {
  if (!BACKEND_URL) {
    console.error('ScanSavvy: Cannot generate QR URL - no base URL configured');
    return null;
  }
  return `${BACKEND_URL}/api/bundle/${bundleId}/view`;
};

// Get current bundle URL (for anonymous users)
const getCurrentBundleUrl = () => {
  if (!BACKEND_URL) {
    console.error('ScanSavvy: Cannot generate QR URL - no base URL configured');
    return null;
  }
  return `${BACKEND_URL}/api/bundle/current/view`;
};

// ============== REAL QR CODE COMPONENT ==============
const QRCodeDisplay = ({ bundleId, size = 256, showLink = true }) => {
  const qrUrl = bundleId ? getQRCodeUrl(bundleId) : getCurrentBundleUrl();
  
  // Guard: Don't render broken QR if URL is invalid
  if (!qrUrl || qrUrl.startsWith('undefined') || qrUrl.startsWith('/api')) {
    return (
      <div className="qr-error-container">
        <div className="qr-error-icon">⚠️</div>
        <p className="qr-error-text">QR code temporarily unavailable</p>
        <p className="qr-error-hint">Your coupons are listed below</p>
      </div>
    );
  }
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(qrUrl).then(() => {
      alert('Link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };
  
  return (
    <div className="qr-code-wrapper">
      <div className="qr-code-container" style={{ background: 'white', padding: '16px', borderRadius: '8px' }}>
        <QRCodeSVG
          value={qrUrl}
          size={size}
          level="M"
          bgColor="#FFFFFF"
          fgColor="#000000"
          includeMargin={false}
        />
      </div>
      {showLink && (
        <div className="qr-link-section">
          <a 
            href={qrUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="qr-open-link"
          >
            🔗 Open coupon bundle
          </a>
          <button 
            onClick={copyToClipboard}
            className="qr-copy-btn"
            title="Copy link"
          >
            📋 Copy link
          </button>
        </div>
      )}
    </div>
  );
};

// ============== COMPONENTS ==============

const Header = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <header className="header">
      <nav className="navbar">
        <div className="logo-container" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
          <img src="/assets/scansavvy-logo.png" alt="ScanSavvy" className="logo-image" />
          <span className="logo-text">ScanSavvy</span>
        </div>
        
        {!user && location.pathname === "/" && (
          <div className="nav-links">
            <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')}>How It Works</a>
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')}>Features</a>
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')}>Pricing</a>
          </div>
        )}
        
        <div className="nav-actions">
          {user ? (
            <>
              <button className="btn-text" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">
                My Coupons
              </button>
              <button className="btn-text" onClick={onLogout} data-testid="nav-logout">
                Sign Out
              </button>
            </>
          ) : (
            <button className="btn-primary" onClick={() => navigate("/onboarding")} data-testid="nav-get-started">
              Get Started
            </button>
          )}
        </div>
      </nav>
    </header>
  );
};

// ============== PAGES ==============

const HomePage = ({ onGetStarted }) => {
  const [zipCode, setZipCode] = useState("");
  const [locationStatus, setLocationStatus] = useState("idle");
  const [nearbyStores, setNearbyStores] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const allStores = [
    { id: "walmart", name: "Walmart", coupons: 7, color: "#0071DC" },
    { id: "target", name: "Target", coupons: 6, color: "#CC0000" },
    { id: "kroger", name: "Kroger", coupons: 5, color: "#0C3E80" },
    { id: "cvs", name: "CVS", coupons: 5, color: "#CC0000" },
    { id: "walgreens", name: "Walgreens", coupons: 4, color: "#E31837" },
    { id: "costco", name: "Costco", coupons: 4, color: "#E31837" },
    { id: "publix", name: "Publix", coupons: 4, color: "#3B8C3B" },
    { id: "aldi", name: "Aldi", coupons: 3, color: "#00529B" },
    { id: "safeway", name: "Safeway", coupons: 3, color: "#E8322E" },
    { id: "whole-foods", name: "Whole Foods", coupons: 3, color: "#00674B" },
  ];

  const handleGetLocation = () => {
    setLocationStatus("loading");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setTimeout(() => {
            const shuffled = [...allStores].sort(() => 0.5 - Math.random());
            setNearbyStores(shuffled.slice(0, 6 + Math.floor(Math.random() * 3)));
            setLocationStatus("found");
          }, 1000);
        },
        () => setLocationStatus("error")
      );
    } else {
      setLocationStatus("error");
    }
  };

  const handleZipSubmit = (e) => {
    e.preventDefault();
    if (zipCode.length === 5) {
      setLocationStatus("loading");
      setTimeout(() => {
        const shuffled = [...allStores].sort(() => 0.5 - Math.random());
        setNearbyStores(shuffled.slice(0, 6 + Math.floor(Math.random() * 3)));
        setLocationStatus("found");
      }, 800);
    }
  };

  return (
    <main className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 data-testid="hero-title">All Your Coupons.<br/>One QR Code.</h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Select your stores, get every available coupon bundled into one weekly QR code. 
            At checkout, scan once and save instantly.
          </p>
          <div className="hero-cta-group">
            <button className="btn-primary btn-xl" onClick={() => navigate('/onboarding')} data-testid="hero-cta">
              Get My Weekly Coupons
            </button>
            <button className="btn-secondary-outline" onClick={() => setShowLocationModal(true)} data-testid="hero-location">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              Find Stores Near Me
            </button>
          </div>
          <div className="hero-proof">
            <div className="proof-avatars">
              <div className="avatar">J</div>
              <div className="avatar">S</div>
              <div className="avatar">M</div>
              <div className="avatar">+</div>
            </div>
            <span>Join 50,000+ smart shoppers saving weekly</span>
          </div>
        </div>
        <div className="hero-visual">
          <div className="phone-frame">
            <div className="phone-notch"></div>
            <div className="phone-content">
              <div className="app-bar">
                <img src="/assets/scansavvy-logo.png" alt="" className="app-bar-logo" />
                <span>My Coupons</span>
              </div>
              <div className="coupon-card-demo">
                <div className="card-header">
                  <span className="week-badge">Week of March 17</span>
                </div>
                <div className="qr-demo">
                  <QRCodeSVG value="https://scansavvy.app" size={100} bgColor="#FFFFFF" fgColor="#000000" />
                </div>
                <div className="card-savings">
                  <span className="savings-amount">$47.50</span>
                  <span className="savings-label">estimated savings</span>
                </div>
                <div className="store-count-badge">12 coupons • 4 stores</div>
              </div>
            </div>
          </div>
          <div className="floating-alert">
            <div className="alert-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
            </div>
            <div className="alert-text">
              <strong>Weekly bundle ready!</strong>
              <span>Scan to save $47.50</span>
            </div>
          </div>
        </div>
      </section>

      {/* Store Partners Section */}
      <section className="partners-section">
        <p className="partners-label">Coupons available at these stores and more</p>
        <div className="partners-logos">
          <div className="partner-logo" style={{backgroundColor: '#0071DC'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Walmart</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#CC0000'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Target</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#0C3E80'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Kroger</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#CC0000'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>CVS</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#E31837'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Walgreens</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#E31837'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Costco</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#3B8C3B'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>Publix</span>
          </div>
          <div className="partner-logo" style={{backgroundColor: '#00529B'}}>
            <span className="partner-name" style={{color: 'white', fontWeight: '700'}}>ALDI</span>
          </div>
        </div>
        <p className="partners-count">+ 12 more retailers</p>
      </section>

      {/* Popular in Your Area Section */}
      <section className="popular-section" data-testid="popular-section">
        <div className="section-intro">
          <h2 className="section-title">Popular in Your Area</h2>
          <p className="section-desc">See what shoppers near you are saving on this week</p>
        </div>
        
        <div className="popular-grid">
          <div className="trending-deals">
            <h3 className="popular-heading">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              Trending Deals
            </h3>
            <div className="deal-cards">
              <div className="deal-card">
                <div className="deal-badge hot">HOT</div>
                <div className="deal-store-name" style={{color: '#CC0000'}}>Target</div>
                <div className="deal-offer">25% OFF</div>
                <div className="deal-desc">All household essentials</div>
                <div className="deal-claimed">2,340 claimed today</div>
              </div>
              <div className="deal-card">
                <div className="deal-badge new">NEW</div>
                <div className="deal-store-name" style={{color: '#0071DC'}}>Walmart</div>
                <div className="deal-offer">$10 OFF $50</div>
                <div className="deal-desc">Grocery purchases</div>
                <div className="deal-claimed">1,892 claimed today</div>
              </div>
              <div className="deal-card">
                <div className="deal-store-name" style={{color: '#0C3E80'}}>Kroger</div>
                <div className="deal-offer">BOGO Free</div>
                <div className="deal-desc">Select snacks & beverages</div>
                <div className="deal-claimed">1,567 claimed today</div>
              </div>
              <div className="deal-card">
                <div className="deal-store-name" style={{color: '#CC0000'}}>CVS</div>
                <div className="deal-offer">40% OFF</div>
                <div className="deal-desc">Health & wellness items</div>
                <div className="deal-claimed">987 claimed today</div>
              </div>
            </div>
          </div>
          
          <div className="local-stats">
            <h3 className="popular-heading">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              Your Area This Week
            </h3>
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-value">$47,230</div>
                <div className="stat-label">Saved by local shoppers</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">12,450</div>
                <div className="stat-label">Coupons redeemed</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">$18.92</div>
                <div className="stat-label">Avg savings per trip</div>
              </div>
            </div>
            
            <div className="top-savers social-feed">
              <h4>Activity Near You</h4>
              <div className="saver-list">
                <div className="saver-item feed-card">
                  <div className="saver-avatar" style={{backgroundColor: '#1F2A44'}}>S</div>
                  <div className="saver-info">
                    <span className="saver-name">Sarah M. <span className="saver-action">saved</span> <span className="saver-highlight">$156</span></span>
                    <span className="saver-time">this month at Target, Kroger</span>
                  </div>
                </div>
                <div className="feed-divider"></div>
                <div className="saver-item feed-card">
                  <div className="saver-avatar" style={{backgroundColor: '#2E3B5A'}}>J</div>
                  <div className="saver-info">
                    <span className="saver-name">James T. <span className="saver-action">saved</span> <span className="saver-highlight">$142</span></span>
                    <span className="saver-time">this month at Walmart, CVS</span>
                  </div>
                </div>
                <div className="feed-divider"></div>
                <div className="saver-item feed-card">
                  <div className="saver-avatar" style={{backgroundColor: '#1F2A44'}}>M</div>
                  <div className="saver-info">
                    <span className="saver-name">Maria L. <span className="saver-action">saved</span> <span className="saver-highlight">$128</span></span>
                    <span className="saver-time">this month at Publix, Costco</span>
                  </div>
                </div>
                <div className="feed-divider"></div>
                <div className="saver-item feed-card">
                  <div className="saver-avatar" style={{backgroundColor: '#2E3B5A'}}>D</div>
                  <div className="saver-info">
                    <span className="saver-name">David R. <span className="saver-action">saved</span> <span className="saver-highlight">$98</span></span>
                    <span className="saver-time">this week at Walgreens, ALDI</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="popular-cta">
          <button className="btn-primary btn-xl" onClick={() => setShowLocationModal(true)}>
            See Deals in My Area
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="section-intro">
          <h2 className="section-title" data-testid="how-it-works-title">How It Works</h2>
          <p className="section-desc">Four simple steps to automatic savings</p>
        </div>
        <div className="process-steps">
          <div className="process-step" data-testid="step-1">
            <div className="step-num">1</div>
            <div className="step-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <h3>Find Stores Near You</h3>
            <p>Enter your ZIP code or share your location to see which stores have coupons in your area.</p>
          </div>
          <div className="step-line"></div>
          <div className="process-step" data-testid="step-2">
            <div className="step-num">2</div>
            <div className="step-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12l2 2 4-4"/>
                <rect x="3" y="3" width="18" height="18" rx="2"/>
              </svg>
            </div>
            <h3>Select Your Stores</h3>
            <p>Choose your favorite stores. We'll track all available coupons for those stores automatically.</p>
          </div>
          <div className="step-line"></div>
          <div className="process-step" data-testid="step-3">
            <div className="step-num">3</div>
            <div className="step-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <path d="M14 14h7v7"/>
              </svg>
            </div>
            <h3>Get Your Weekly QR</h3>
            <p>Every Sunday, we bundle ALL your coupons into ONE QR code and send it to your phone.</p>
          </div>
          <div className="step-line"></div>
          <div className="process-step" data-testid="step-4">
            <div className="step-num">4</div>
            <div className="step-icon-wrap green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
              </svg>
            </div>
            <h3>Scan Once & Save</h3>
            <p>Show your single QR code at checkout. All your coupons apply instantly. It's that easy.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="section-intro">
          <h2 className="section-title" data-testid="features-title">Why ScanSavvy?</h2>
          <p className="section-desc">Save time and money with smart coupon delivery</p>
        </div>
        <div className="features-grid">
          <div className="feature-item" data-testid="feature-automatic">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <h3>Weekly Auto-Updates</h3>
            <p>Fresh coupons bundled every Sunday at midnight. No searching, no clipping, no effort required.</p>
          </div>
          <div className="feature-item" data-testid="feature-one-qr">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
              </svg>
            </div>
            <h3>One QR = All Coupons</h3>
            <p>Every coupon from every store bundled into a single scannable code. Fast checkout, maximum savings.</p>
          </div>
          <div className="feature-item" data-testid="feature-location">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <h3>Location-Based</h3>
            <p>See which stores have coupons near you. Only get deals for stores you actually visit.</p>
          </div>
          <div className="feature-item" data-testid="feature-brands">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 7h-9M14 17H5"/>
                <circle cx="17" cy="17" r="3"/>
                <circle cx="7" cy="7" r="3"/>
              </svg>
            </div>
            <h3>Brand Coupons Too</h3>
            <p>Get manufacturer coupons from top brands that work at any participating store.</p>
          </div>
          <div className="feature-item" data-testid="feature-alerts">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
            </div>
            <h3>Smart Alerts</h3>
            <p>Get notified when new bundles are ready. Never miss a deal.</p>
          </div>
          <div className="feature-item" data-testid="feature-free">
            <div className="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h3>Free to Start</h3>
            <p>No credit card required. Start saving immediately with our free plan.</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="section-intro">
          <h2 className="section-title" data-testid="pricing-title">Simple Pricing</h2>
          <p className="section-desc">Plans that pay for themselves in savings</p>
        </div>
        <div className="pricing-grid">
          <div className="price-card" data-testid="pricing-free">
            <h3>Free</h3>
            <div className="price-amount">$0<span>/mo</span></div>
            <p className="price-tagline">Get started for free</p>
            <ul className="price-features">
              <li>Up to 3 stores</li>
              <li>1 weekly QR bundle (Sundays)</li>
              <li>Push notifications</li>
              <li>Basic savings dashboard</li>
            </ul>
            <button className="btn-secondary btn-block" onClick={onGetStarted}>Start Free</button>
          </div>
          <div className="price-card featured" data-testid="pricing-premium">
            <div className="price-badge">Popular</div>
            <h3>Premium</h3>
            <div className="price-amount">$3.99<span>/mo</span></div>
            <p className="price-tagline">For regular shoppers</p>
            <ul className="price-features">
              <li>Unlimited stores</li>
              <li>Multiple QR refreshes/week</li>
              <li>Manufacturer coupons</li>
              <li>Personalized recommendations</li>
              <li>Priority high-value deals</li>
              <li>Full savings dashboard</li>
            </ul>
            <button className="btn-primary btn-block" onClick={onGetStarted}>Start 14-Day Trial</button>
          </div>
          <div className="price-card" data-testid="pricing-family">
            <h3>Family</h3>
            <div className="price-amount">$5.99<span>/mo</span></div>
            <p className="price-tagline">For households</p>
            <ul className="price-features">
              <li>Everything in Premium</li>
              <li>Up to 5 members</li>
              <li>Shared coupon bundles</li>
              <li>Family spending insights</li>
            </ul>
            <button className="btn-secondary btn-block" onClick={onGetStarted}>Get Family</button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="cta-inner">
          <h2>Start Saving Today</h2>
          <p>Find stores near you, pick your favorites, and get your first weekly QR code.</p>
          <button className="btn-primary btn-xl btn-white" onClick={() => setShowLocationModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            Find Stores Near Me
          </button>
          <span className="cta-sub">Free forever plan • No credit card required</span>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer light-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="footer-logo-wrap">
              <img src="/assets/scansavvy-logo.png" alt="ScanSavvy" className="footer-brand-logo" />
              <span>ScanSavvy</span>
            </div>
            <p>All your coupons. One QR code.</p>
          </div>
          <div className="footer-nav">
            <div className="footer-nav-col">
              <h4>Product</h4>
              <a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); }}>Features</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>Pricing</a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>How It Works</a>
            </div>
            <div className="footer-nav-col">
              <h4>Support</h4>
              <a href="mailto:help@scansavvy.com">Help Center</a>
              <a href="mailto:info@scansavvy.com">Contact Us</a>
            </div>
            <div className="footer-nav-col">
              <h4>Legal</h4>
              <a href="/privacy">Privacy</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>Terms</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom-bar">
          <p>© 2026 ScanSavvy. All rights reserved. Built by <a href="https://mjvholdings.com/" target="_blank" rel="noopener noreferrer" className="mjv-link">MJV Holdings</a></p>
        </div>
      </footer>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="location-modal">
            <button className="modal-close" onClick={() => setShowLocationModal(false)}>×</button>
            
            {locationStatus === "idle" && (
              <div className="location-prompt">
                <div className="location-icon-large">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <h3>Find Stores With Coupons</h3>
                <p>We'll show you which stores have coupons available in your area.</p>
                
                <button className="btn-primary btn-block location-btn" onClick={handleGetLocation}>
                  Use My Location
                </button>
                
                <div className="location-divider">
                  <span>or enter ZIP code</span>
                </div>
                
                <form onSubmit={handleZipSubmit} className="zip-form">
                  <input 
                    type="text" 
                    placeholder="Enter ZIP code"
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    data-testid="zip-input"
                  />
                  <button type="submit" className="btn-primary" disabled={zipCode.length !== 5}>
                    Find Stores
                  </button>
                </form>
              </div>
            )}
            
            {locationStatus === "loading" && (
              <div className="location-loading">
                <div className="loading-spinner-large"></div>
                <p>Finding stores near you...</p>
              </div>
            )}
            
            {locationStatus === "found" && (
              <div className="location-results" data-testid="stores-near-you">
                <h3>Stores With Coupons Near You</h3>
                <p className="results-subtitle">{nearbyStores.length} stores found with available coupons</p>
                
                <div className="nearby-stores-list">
                  {nearbyStores.map(store => (
                    <div key={store.id} className="nearby-store-item">
                      <div className="store-info">
                        <span className="store-name-text" style={{color: store.color}}>{store.name}</span>
                        <span className="store-coupon-count">{store.coupons} coupons available</span>
                      </div>
                      <div className="store-coupon-badge">{store.coupons}</div>
                    </div>
                  ))}
                </div>
                
                <button className="btn-primary btn-block" onClick={onGetStarted}>
                  Select My Stores
                </button>
                <button className="btn-link" onClick={() => setLocationStatus("idle")}>
                  ← Try a different location
                </button>
              </div>
            )}
            
            {locationStatus === "error" && (
              <div className="location-error">
                <p>Couldn't detect your location. Please enter your ZIP code instead.</p>
                <form onSubmit={handleZipSubmit} className="zip-form">
                  <input 
                    type="text" 
                    placeholder="Enter ZIP code"
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                  />
                  <button type="submit" className="btn-primary" disabled={zipCode.length !== 5}>
                    Find Stores
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

// Onboarding Page
// Default fallback stores if API fails
const DEFAULT_STORES = [
  { id: "walmart", name: "Walmart", logo: "🛒", color: "#0071DC", category: "Superstore" },
  { id: "target", name: "Target", logo: "🎯", color: "#CC0000", category: "Superstore" },
  { id: "kroger", name: "Kroger", logo: "🥬", color: "#0C3E80", category: "Grocery" },
  { id: "publix", name: "Publix", logo: "🛍️", color: "#3B8C3B", category: "Grocery" },
  { id: "cvs", name: "CVS", logo: "💊", color: "#CC0000", category: "Pharmacy" },
  { id: "walgreens", name: "Walgreens", logo: "💊", color: "#E31837", category: "Pharmacy" },
  { id: "dollar-general", name: "Dollar General", logo: "💵", color: "#FFC220", category: "Discount" },
  { id: "costco", name: "Costco", logo: "📦", color: "#E31837", category: "Wholesale" },
  { id: "aldi", name: "Aldi", logo: "🛒", color: "#00529B", category: "Grocery" },
  { id: "safeway", name: "Safeway", logo: "🥗", color: "#E8322E", category: "Grocery" },
  { id: "whole-foods", name: "Whole Foods", logo: "🥑", color: "#00674B", category: "Grocery" },
  { id: "trader-joes", name: "Trader Joe's", logo: "🌻", color: "#B5121B", category: "Grocery" },
];

// Popular store IDs for quick selection
const POPULAR_STORE_IDS = ["walmart", "target", "publix", "kroger", "cvs", "walgreens", "dollar-general"];

// Key for tracking if user has made selections in this session
const ONBOARDING_SESSION_KEY = 'scansavvy_onboarding_session';

const OnboardingPage = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedStores, setSelectedStores] = useState([]); // Always start empty
  const [manufacturerCoupons, setManufacturerCoupons] = useState(false);
  const [notificationMethod, setNotificationMethod] = useState("push");
  const [stores, setStores] = useState(DEFAULT_STORES);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({}); // For inline validation
  const [locationStatus, setLocationStatus] = useState("idle");
  const [nearbyLabel, setNearbyLabel] = useState(false);
  const navigate = useNavigate();
  
  // Load stores from API with fallback - DO NOT auto-restore selections
  useEffect(() => {
    const fetchStores = async () => {
      setStoresLoading(true);
      try {
        const response = await axios.get(`${API}/stores`);
        const apiStores = response.data?.stores;
        if (apiStores && Array.isArray(apiStores) && apiStores.length > 0) {
          setStores(apiStores);
        } else {
          setStores(DEFAULT_STORES);
        }
      } catch (e) {
        console.error("Failed to fetch stores, using defaults", e);
        setStores(DEFAULT_STORES);
      } finally {
        setStoresLoading(false);
      }
    };
    fetchStores();
    
    // Only restore selections if user explicitly saved them in a PREVIOUS completed session
    // Check for a flag that indicates completed onboarding
    const hasCompletedOnboarding = loadFromLocalStorage('scansavvy_onboarding_completed', false);
    if (hasCompletedOnboarding) {
      const savedStores = loadFromLocalStorage(STORAGE_KEYS.SELECTED_STORES, []);
      if (savedStores && Array.isArray(savedStores) && savedStores.length > 0) {
        setSelectedStores(savedStores);
      }
    }
    // If no completed onboarding, start fresh with empty selection
  }, []);
  
  // Save selected stores to localStorage only when user makes changes (not on initial load)
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  useEffect(() => {
    if (userHasInteracted && selectedStores.length >= 0) {
      saveToLocalStorage(STORAGE_KEYS.SELECTED_STORES, selectedStores);
    }
  }, [selectedStores, userHasInteracted]);
  
  // Filter stores based on search term
  const allStores = stores || DEFAULT_STORES;
  const filteredStores = searchTerm.trim() 
    ? allStores.filter(store => 
        store.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];
  
  // Popular stores (always visible)
  const popularStores = allStores.filter(store => 
    POPULAR_STORE_IDS.includes(store.id)
  );
  
  // All other stores (for "More Stores" section)
  const otherStores = allStores.filter(store => 
    !POPULAR_STORE_IDS.includes(store.id)
  );
  
  // Get store details by ID
  const getStoreById = (storeId) => {
    return allStores.find(s => s.id === storeId) || { id: storeId, name: storeId, logo: "🏪", color: "#666" };
  };
  
  const toggleStore = (storeId) => {
    setUserHasInteracted(true);
    setSelectedStores(prev => {
      const current = prev || [];
      // Prevent duplicates
      if (current.includes(storeId)) {
        return current.filter(id => id !== storeId);
      } else {
        return [...current, storeId];
      }
    });
    setError(""); // Clear any error when user interacts
  };
  
  const removeStore = (storeId) => {
    setUserHasInteracted(true);
    setSelectedStores(prev => (prev || []).filter(id => id !== storeId));
  };
  
  // Handle location-based store sorting (optional enhancement)
  const handleGetLocation = () => {
    setLocationStatus("loading");
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setTimeout(() => {
            setNearbyLabel(true);
            setLocationStatus("found");
          }, 800);
        },
        () => {
          setLocationStatus("denied");
          setTimeout(() => setLocationStatus("idle"), 2000);
        },
        { timeout: 5000 }
      );
    } else {
      setLocationStatus("denied");
      setTimeout(() => setLocationStatus("idle"), 2000);
    }
  };
  
  // Validate email
  const validateEmail = (emailStr) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  };
  
  // Handle final submission with MVP fallback
  const handleSubmit = async () => {
    setFieldErrors({});
    setError("");
    
    // Validate email if selected
    if (notificationMethod === 'email' && !validateEmail(email)) {
      setFieldErrors({ email: "Please enter a valid email address" });
      return;
    }
    
    if (selectedStores.length === 0) {
      setError("Please select at least one store first");
      return;
    }
    
    setLoading(true);
    
    try {
      // Try to create user via API
      const userRes = await axios.post(`${API}/users`, { name, email });
      const userId = userRes.data.id;
      
      await axios.put(`${API}/users/${userId}/stores`, { store_ids: selectedStores });
      await axios.put(`${API}/users/${userId}/manufacturer-coupons`, { enabled: manufacturerCoupons });
      await axios.put(`${API}/users/${userId}/notification-method`, { method: notificationMethod });
      
      // Create a real Supabase bundle
      const newBundle = await createSupabaseBundle(selectedStores, name, email);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, newBundle);
      
      const updatedUser = await axios.get(`${API}/users/${userId}`);
      
      // Mark onboarding as completed
      saveToLocalStorage('scansavvy_onboarding_completed', true);
      saveToLocalStorage(STORAGE_KEYS.SELECTED_STORES, selectedStores);
      
      onComplete(updatedUser.data);
    } catch (e) {
      console.error("API error, falling back to Supabase bundle", e);
      
      // Try Supabase bundle even if MongoDB fails
      try {
        const newBundle = await createSupabaseBundle(selectedStores, name, email);
        saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, newBundle);
        saveToLocalStorage(STORAGE_KEYS.SELECTED_STORES, selectedStores);
        saveToLocalStorage('scansavvy_onboarding_completed', true);
        saveToLocalStorage('scansavvy_notification_method', notificationMethod);
        navigate('/bundle');
      } catch (supabaseError) {
        console.error("Supabase also failed, using local fallback", supabaseError);
        const localBundle = generateLocalBundle(selectedStores);
        saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, localBundle);
        saveToLocalStorage(STORAGE_KEYS.SELECTED_STORES, selectedStores);
        saveToLocalStorage('scansavvy_onboarding_completed', true);
        navigate('/bundle');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle notification method change
  const handleNotificationChange = (method) => {
    setNotificationMethod(method);
    setFieldErrors({}); // Clear field errors when switching
  };
  
  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1</div>
          <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2</div>
          <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3</div>
        </div>
        
        {step === 1 && (
          <div className="onboarding-step" data-testid="onboarding-step-1">
            <h2>Let's get you set up</h2>
            <p className="step-subtitle">We just need a few details to start sending you coupons.</p>
            
            <div className="form-group">
              <label htmlFor="name">Your Name</label>
              <input 
                type="text" 
                id="name"
                value={name} 
                onChange={e => setName(e.target.value)}
                placeholder="Enter your name"
                data-testid="input-name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input 
                type="email" 
                id="email"
                value={email} 
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="input-email"
              />
            </div>
            
            {error && <p className="error-message">{error}</p>}
            
            <button 
              className="btn-primary btn-full" 
              onClick={() => {
                if (name.trim() && email.trim()) {
                  setError("");
                  setStep(2);
                } else {
                  setError("Please fill in all fields");
                }
              }}
              data-testid="btn-next-step"
            >
              Continue
            </button>
          </div>
        )}
        
        {step === 2 && (
          <div className="onboarding-step step-2-stores" data-testid="onboarding-step-2">
            <h2>Pick your stores</h2>
            <p className="step-subtitle">Tap the stores you shop at. We'll bundle ALL their coupons into your weekly QR.</p>
            
            {/* Selected Stores Pills */}
            {selectedStores.length > 0 && (
              <div className="selected-stores-section" data-testid="selected-stores-section">
                <div className="selected-stores-header">
                  <span className="selected-badge">{selectedStores.length} selected</span>
                </div>
                <div className="selected-stores-pills">
                  {selectedStores.map(storeId => {
                    const store = getStoreById(storeId);
                    return (
                      <div 
                        key={storeId} 
                        className="store-pill"
                        style={{ borderColor: store.color }}
                        data-testid={`selected-pill-${storeId}`}
                      >
                        <span className="pill-logo">{store.logo}</span>
                        <span className="pill-name">{store.name}</span>
                        <button 
                          className="pill-remove" 
                          onClick={() => removeStore(storeId)}
                          aria-label={`Remove ${store.name}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Search Bar with Dropdown */}
            <div className="store-search-container">
              <div className="search-box-enhanced">
                <svg className="search-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input 
                  type="text"
                  placeholder="Search for a store..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  data-testid="store-search"
                  autoComplete="off"
                />
                {searchTerm && (
                  <button 
                    className="search-clear" 
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {searchTerm.trim() && (
                <div className="search-dropdown" data-testid="search-dropdown">
                  {filteredStores.length > 0 ? (
                    filteredStores.map(store => (
                      <div 
                        key={store.id}
                        className={`search-result-item ${selectedStores.includes(store.id) ? 'selected' : ''}`}
                        onClick={() => {
                          toggleStore(store.id);
                          setSearchTerm("");
                        }}
                        data-testid={`search-result-${store.id}`}
                      >
                        <span className="result-logo" style={{ backgroundColor: store.color }}>{store.logo}</span>
                        <span className="result-name">{store.name}</span>
                        {selectedStores.includes(store.id) && (
                          <span className="result-check">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="search-no-results">No stores found for "{searchTerm}"</div>
                  )}
                </div>
              )}
            </div>
            
            {/* Optional Location Button */}
            <div className="location-row">
              <button 
                className={`location-btn ${locationStatus === 'found' ? 'found' : ''}`}
                onClick={handleGetLocation}
                disabled={locationStatus === 'loading'}
                data-testid="location-btn"
              >
                {locationStatus === 'loading' ? (
                  <>
                    <span className="spinner-small"></span>
                    Finding nearby stores...
                  </>
                ) : locationStatus === 'found' ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    Showing stores near you
                  </>
                ) : locationStatus === 'denied' ? (
                  <>Location unavailable</>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    Show stores near me
                  </>
                )}
              </button>
            </div>
            
            {/* Popular Stores Section */}
            <div className="stores-section">
              <h3 className="stores-section-title">
                {nearbyLabel ? '📍 Popular Near You' : '⭐ Popular Stores'}
              </h3>
              <p className="stores-section-subtitle">Tap to select</p>
              
              {storesLoading ? (
                <div className="stores-loading">
                  <div className="spinner-small"></div>
                  <span>Loading stores...</span>
                </div>
              ) : (
                <div className="store-grid-enhanced" data-testid="popular-store-grid">
                  {popularStores.map(store => (
                    <div 
                      key={store.id}
                      className={`store-card-enhanced ${selectedStores.includes(store.id) ? 'selected' : ''}`}
                      onClick={() => toggleStore(store.id)}
                      style={{ '--store-color': store.color }}
                      data-testid={`store-${store.id}`}
                    >
                      <div className="store-card-logo" style={{ backgroundColor: store.color }}>
                        {store.logo}
                      </div>
                      <div className="store-card-name">{store.name}</div>
                      <div className="store-card-check">
                        {selectedStores.includes(store.id) ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        ) : (
                          <div className="store-card-circle"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* More Stores Section */}
            {otherStores.length > 0 && (
              <div className="stores-section more-stores">
                <h3 className="stores-section-title">More Stores</h3>
                <div className="store-grid-enhanced compact" data-testid="more-store-grid">
                  {otherStores.map(store => (
                    <div 
                      key={store.id}
                      className={`store-card-enhanced ${selectedStores.includes(store.id) ? 'selected' : ''}`}
                      onClick={() => toggleStore(store.id)}
                      style={{ '--store-color': store.color }}
                      data-testid={`store-${store.id}`}
                    >
                      <div className="store-card-logo" style={{ backgroundColor: store.color }}>
                        {store.logo}
                      </div>
                      <div className="store-card-name">{store.name}</div>
                      <div className="store-card-check">
                        {selectedStores.includes(store.id) ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        ) : (
                          <div className="store-card-circle"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {error && <p className="error-message">{error}</p>}
            
            {/* Fixed Bottom Actions */}
            <div className="step-actions-fixed">
              <button className="btn-text" onClick={() => setStep(1)} data-testid="btn-back">
                ← Back
              </button>
              <button 
                className={`btn-primary btn-continue ${selectedStores.length === 0 ? 'disabled' : ''}`}
                onClick={() => {
                  if (selectedStores.length > 0) {
                    setError("");
                    setStep(3);
                  } else {
                    setError("Please select at least one store");
                  }
                }}
                disabled={selectedStores.length === 0}
                data-testid="btn-continue-stores"
              >
                {selectedStores.length === 0 
                  ? 'Select stores to continue' 
                  : `Continue with ${selectedStores.length} store${selectedStores.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="onboarding-step" data-testid="onboarding-step-3">
            <h2>Almost done!</h2>
            <p className="step-subtitle">Choose how you'd like to receive your weekly coupon bundle.</p>
            
            <div className="option-card" data-testid="manufacturer-toggle">
              <div className="option-info">
                <h4>🏭 Manufacturer Coupons</h4>
                <p>Get extra brand coupons (Coca-Cola, Kraft, P&G, etc.) that work at any retailer.</p>
              </div>
              <label className="toggle">
                <input 
                  type="checkbox" 
                  checked={manufacturerCoupons}
                  onChange={e => setManufacturerCoupons(e.target.checked)}
                  data-testid="checkbox-manufacturer"
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            
            <div className="option-section">
              <h4>How should we notify you when your weekly QR is ready?</h4>
              <div className="notification-options-enhanced" data-testid="notification-options">
                <div 
                  className={`notification-card ${notificationMethod === 'push' ? 'selected' : ''}`}
                  onClick={() => handleNotificationChange('push')}
                  data-testid="notification-push"
                >
                  <div className="notification-card-icon">📲</div>
                  <div className="notification-card-content">
                    <span className="notification-card-title">Push Notification</span>
                    <span className="notification-card-desc">Get notified instantly on your device</span>
                  </div>
                  <div className="notification-card-check">
                    {notificationMethod === 'push' && (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    )}
                  </div>
                </div>
                
                <div 
                  className={`notification-card ${notificationMethod === 'email' ? 'selected' : ''}`}
                  onClick={() => handleNotificationChange('email')}
                  data-testid="notification-email"
                >
                  <div className="notification-card-icon">📧</div>
                  <div className="notification-card-content">
                    <span className="notification-card-title">Email</span>
                    <span className="notification-card-desc">Weekly email with your coupon bundle</span>
                  </div>
                  <div className="notification-card-check">
                    {notificationMethod === 'email' && (
                      <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              
              {/* In-app availability note */}
              <div className="inapp-availability-note">
                <span className="note-icon">📱</span>
                <span className="note-text">Your weekly QR will always be available in-app on your Dashboard</span>
              </div>
              
              {/* Conditional hint for Email */}
              {notificationMethod === 'email' && (
                <div className="conditional-input-section" data-testid="email-confirm-section">
                  <div className="email-confirm-box">
                    <span className="email-icon">✉️</span>
                    <div className="email-confirm-text">
                      <span className="email-address">{email || 'your@email.com'}</span>
                      <span className="email-hint">We'll send your weekly QR to this email</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hint for Push */}
              {notificationMethod === 'push' && (
                <div className="conditional-input-section" data-testid="push-hint-section">
                  <div className="push-hint-box">
                    <span className="push-icon">🔔</span>
                    <span className="push-hint-text">We'll ask for notification permission when your first bundle is ready</span>
                  </div>
                </div>
              )}
            </div>
            
            {error && <p className="error-message">{error}</p>}
            
            {/* Summary */}
            <div className="onboarding-summary">
              <h4>Your Setup</h4>
              <div className="summary-row">
                <span className="summary-label">Stores:</span>
                <span className="summary-value">{selectedStores.length} selected</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Notifications:</span>
                <span className="summary-value">
                  {notificationMethod === 'push' && '📲 Push'}
                  {notificationMethod === 'email' && '📧 Email'}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Manufacturer coupons:</span>
                <span className="summary-value">{manufacturerCoupons ? '✓ Included' : '✗ Not included'}</span>
              </div>
            </div>
            
            <div className="step-actions">
              <button className="btn-text" onClick={() => setStep(2)} data-testid="btn-back-step3">
                ← Back
              </button>
              <button 
                className="btn-primary btn-finish" 
                onClick={handleSubmit}
                disabled={loading}
                data-testid="btn-finish-setup"
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span>
                    Setting up...
                  </>
                ) : (
                  "Get My Coupons →"
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Dashboard Page - NEW SINGLE QR DESIGN
const DashboardPage = ({ user, onUpdateUser }) => {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("coupons");
  const [showCoupons, setShowCoupons] = useState(false);
  const [stores, setStores] = useState([]);
  const [editingStores, setEditingStores] = useState(false);
  const [tempSelectedStores, setTempSelectedStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  
  const fetchBundle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/bundle`);
      if (response.data.has_bundle) {
        setBundle(response.data.bundle);
      } else {
        setBundle(null);
      }
    } catch (e) {
      console.error("Failed to fetch bundle", e);
    } finally {
      setLoading(false);
    }
  }, [user.id]);
  
  const fetchStores = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data.stores);
    } catch (e) {
      console.error("Failed to fetch stores", e);
    }
  }, []);
  
  useEffect(() => {
    fetchBundle();
    fetchStores();
  }, [fetchBundle, fetchStores]);
  
  const handleRefreshBundle = async () => {
    setRefreshing(true);
    try {
      // Create a real Supabase bundle
      const newBundle = await createSupabaseBundle(
        user.selected_stores || ['kroger'],
        user.name || 'Guest',
        user.email
      );
      setBundle(newBundle);
      saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, newBundle);
    } catch (e) {
      console.error("Failed to refresh bundle", e);
    } finally {
      setRefreshing(false);
    }
  };
  
  const toggleManufacturerCoupons = async () => {
    try {
      const response = await axios.put(`${API}/users/${user.id}/manufacturer-coupons`, {
        enabled: !user.manufacturer_coupons_enabled
      });
      onUpdateUser(response.data);
      fetchBundle();
    } catch (e) {
      console.error("Failed to toggle manufacturer coupons", e);
    }
  };
  
  const updateNotificationMethod = async (method) => {
    try {
      const response = await axios.put(`${API}/users/${user.id}/notification-method`, { method });
      onUpdateUser(response.data);
    } catch (e) {
      console.error("Failed to update notification method", e);
    }
  };
  
  const saveStoreSelections = async () => {
    try {
      const response = await axios.put(`${API}/users/${user.id}/stores`, {
        store_ids: tempSelectedStores
      });
      onUpdateUser(response.data);
      setEditingStores(false);
      fetchBundle();
    } catch (e) {
      console.error("Failed to update stores", e);
    }
  };
  
  const startEditingStores = () => {
    setTempSelectedStores([...user.selected_stores]);
    setEditingStores(true);
  };
  
  const toggleTempStore = (storeId) => {
    setTempSelectedStores(prev => {
      const current = prev || [];
      return current.includes(storeId) 
        ? current.filter(id => id !== storeId)
        : [...current, storeId];
    });
  };
  
  const filteredStores = (stores || []).filter(store => 
    store.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleSaveQR = () => {
    if (bundle?.qr_url) {
      // Open QR page in new tab for saving
      window.open(bundle.qr_url, '_blank');
    }
  };
  
  const formatValidUntil = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-spinner">Loading your coupons...</div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="welcome-section">
            <h1 data-testid="dashboard-title">Hi, {user.name}! 👋</h1>
            <p className="tier-badge">{user.tier === 'premium' ? '⭐ Premium' : '🆓 Free Plan'}</p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="dashboard-tabs">
          <button 
            className={`tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveTab('coupons')}
            data-testid="tab-coupons"
          >
            📱 My Coupons
          </button>
          <button 
            className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="tab-settings"
          >
            ⚙️ Settings
          </button>
        </div>
        
        {/* Coupons Tab - SINGLE MASTER QR */}
        {activeTab === 'coupons' && (
          <div className="coupons-tab" data-testid="coupons-view">
            {!bundle ? (
              <div className="empty-state">
                <div className="empty-icon">📱</div>
                <h3>No Active Bundle</h3>
                <p>Select stores in Settings to get your coupon bundle.</p>
                <button className="btn-primary" onClick={() => setActiveTab('settings')}>
                  Add Stores
                </button>
              </div>
            ) : (
              <div className="master-bundle-card" data-testid="master-bundle">
                {/* Bundle Header */}
                <div className="bundle-header">
                  <div className="bundle-week">
                    <span className="week-label">{bundle.week_label}</span>
                    <span className="bundle-type-badge">
                      {bundle.bundle_type === 'starter' ? '🚀 Starter Bundle' : '📅 Weekly Bundle'}
                    </span>
                  </div>
                  <div className="bundle-expiry">
                    Valid until {formatValidUntil(bundle.valid_until)}
                  </div>
                </div>
                
                {/* QR Code - MAIN FOCUS */}
                <div className="qr-section">
                  <div className="qr-wrapper">
                    <QRCodeDisplay bundleId={bundle.id} size={256} />
                  </div>
                  <p className="qr-instruction">Scan this QR code at checkout</p>
                  
                  {/* Fallback URL */}
                  <div className="qr-fallback">
                    <a 
                      href={getQRCodeUrl(bundle.id)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="qr-url-link"
                    >
                      {getQRCodeUrl(bundle.id)}
                    </a>
                  </div>
                  
                  {/* Test QR Button */}
                  <button 
                    className="btn-secondary test-qr-btn"
                    onClick={() => window.open(getQRCodeUrl(bundle.id), '_blank')}
                    data-testid="test-qr-btn"
                  >
                    🔗 Test QR Code Link
                  </button>
                </div>
                
                {/* Stats */}
                <div className="bundle-stats">
                  <div className="stat-item">
                    <span className="stat-number">{bundle.coupon_count}</span>
                    <span className="stat-text">Coupons</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <span className="stat-number">${bundle.total_savings}</span>
                    <span className="stat-text">Est. Savings</span>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <span className="stat-number">{bundle.stores_included?.length || 0}</span>
                    <span className="stat-text">Stores</span>
                  </div>
                </div>
                
                {/* Stores Included */}
                <div className="stores-included">
                  <h4>Stores Included</h4>
                  <div className="store-tags">
                    {bundle.stores_included?.map(store => (
                      <span key={store.id} className="store-tag" style={{borderColor: store.color}}>
                        {store.logo} {store.name}
                      </span>
                    ))}
                    {bundle.manufacturer_coupons_included && (
                      <span className="store-tag manufacturer">🏭 Manufacturer</span>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="bundle-actions">
                  <button className="btn-secondary" onClick={handleSaveQR}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Save QR to Phone
                  </button>
                  <button 
                    className="btn-secondary" 
                    onClick={() => setShowCoupons(!showCoupons)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M9 5l7 7-7 7"/>
                    </svg>
                    {showCoupons ? 'Hide' : 'View'} Included Coupons
                  </button>
                </div>
                
                {/* Refresh Button */}
                <button 
                  className="btn-text refresh-btn" 
                  onClick={handleRefreshBundle}
                  disabled={refreshing}
                >
                  {refreshing ? '⟳ Refreshing...' : '⟳ Refresh QR Code'}
                </button>
                
                {/* Coupon List (expandable) */}
                {showCoupons && (
                  <div className="coupon-list-section">
                    <h4>Included Coupons ({bundle.coupon_count})</h4>
                    <div className="coupon-list">
                      {bundle.coupons?.map(coupon => (
                        <div key={coupon.id} className="coupon-item">
                          <div className="coupon-info">
                            <span className="coupon-store">
                              {coupon.store_id ? coupon.store_id.replace('-', ' ').toUpperCase() : 'MANUFACTURER'}
                            </span>
                            <span className="coupon-title">{coupon.title}</span>
                            <span className="coupon-desc">{coupon.description}</span>
                          </div>
                          <div className="coupon-savings">
                            {coupon.savings_type === 'dollar' && `$${coupon.savings_value}`}
                            {coupon.savings_type === 'percent' && `${coupon.savings_value}%`}
                            {coupon.savings_type === 'bogo' && 'BOGO'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-tab" data-testid="settings-view">
            {/* Store Selection */}
            <div className="settings-section">
              <div className="section-header">
                <h2>🏪 My Stores</h2>
                {!editingStores && (
                  <button className="btn-text" onClick={startEditingStores} data-testid="btn-edit-stores">
                    Edit
                  </button>
                )}
              </div>
              
              {!editingStores ? (
                <div className="selected-stores-list">
                  {user.selected_stores.length === 0 ? (
                    <p className="empty-text">No stores selected yet</p>
                  ) : (
                    <div className="store-chips">
                      {user.selected_stores.map(storeId => {
                        const store = stores.find(s => s.id === storeId);
                        return store ? (
                          <span key={storeId} className="store-chip">
                            {store.logo} {store.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  <button className="btn-secondary" onClick={startEditingStores}>
                    {user.selected_stores.length === 0 ? 'Add Stores' : 'Change Stores'}
                  </button>
                </div>
              ) : (
                <div className="store-editor" data-testid="store-editor">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input 
                      type="text"
                      placeholder="Search stores..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="store-grid compact">
                    {filteredStores.map(store => (
                      <div 
                        key={store.id}
                        className={`store-card ${tempSelectedStores.includes(store.id) ? 'selected' : ''}`}
                        onClick={() => toggleTempStore(store.id)}
                      >
                        <div className="store-logo">{store.logo}</div>
                        <div className="store-name">{store.name}</div>
                        {tempSelectedStores.includes(store.id) && (
                          <div className="store-check">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="editor-actions">
                    <button className="btn-text" onClick={() => setEditingStores(false)}>Cancel</button>
                    <button className="btn-primary" onClick={saveStoreSelections}>
                      Save ({tempSelectedStores.length} stores)
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Manufacturer Coupons Toggle */}
            <div className="settings-section">
              <div className="option-card" data-testid="settings-manufacturer">
                <div className="option-info">
                  <h4>🏭 Manufacturer Coupons</h4>
                  <p>Include brand coupons (Tide, Coca-Cola, etc.) in your bundle.</p>
                </div>
                <label className="toggle">
                  <input 
                    type="checkbox" 
                    checked={user.manufacturer_coupons_enabled}
                    onChange={toggleManufacturerCoupons}
                    data-testid="toggle-manufacturer"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            
            {/* Notification Method */}
            <div className="settings-section">
              <h3>📬 Notification Method</h3>
              <p className="section-desc">How should we notify you when your weekly QR is ready?</p>
              <div className="notification-options" data-testid="settings-notifications">
                <label className={`notification-option ${user.notification_method === 'push' ? 'selected' : ''}`}>
                  <input 
                    type="radio" 
                    name="notification" 
                    value="push"
                    checked={user.notification_method === 'push'}
                    onChange={e => updateNotificationMethod(e.target.value)}
                  />
                  <span className="option-icon">📲</span>
                  <span className="option-label">Push Notification</span>
                </label>
                <label className={`notification-option ${user.notification_method === 'email' ? 'selected' : ''}`}>
                  <input 
                    type="radio" 
                    name="notification" 
                    value="email"
                    checked={user.notification_method === 'email'}
                    onChange={e => updateNotificationMethod(e.target.value)}
                  />
                  <span className="option-icon">📧</span>
                  <span className="option-label">Email</span>
                </label>
              </div>
              <p className="settings-hint">Your weekly QR is always available in-app on your Dashboard</p>
            </div>
            
            {/* Account Info */}
            <div className="settings-section">
              <h3>👤 Account</h3>
              <div className="account-info">
                <p><strong>Name:</strong> {user.name}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>Plan:</strong> {user.tier === 'premium' ? 'Premium' : 'Free'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Privacy Page
const PrivacyPage = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: March 2026</p>
        
        <section>
          <h2>Information We Collect</h2>
          <p>We collect information you provide directly, including your name, email address, and store preferences to deliver personalized coupon bundles.</p>
        </section>
        
        <section>
          <h2>How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Generate personalized weekly coupon bundles</li>
            <li>Send notifications when new bundles are ready</li>
            <li>Improve our coupon recommendations</li>
            <li>Provide customer support</li>
          </ul>
        </section>
        
        <section>
          <h2>Data Security</h2>
          <p>We implement industry-standard security measures to protect your personal information.</p>
        </section>
        
        <section>
          <h2>Contact Us</h2>
          <p>For privacy-related questions, contact us at <a href="mailto:privacy@scansavvy.com">privacy@scansavvy.com</a></p>
        </section>
      </div>
    </div>
  );
};

// ============== QUICK BUNDLE PAGE (ZERO FRICTION) ==============
// Works without login - stores selection in local storage

const QuickBundlePage = () => {
  const [selectedStores, setSelectedStores] = useState(() => 
    loadFromLocalStorage(STORAGE_KEYS.SELECTED_STORES, ['kroger'])
  );
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [showCoupons, setShowCoupons] = useState(true);
  
  const availableStores = [
    { id: 'kroger', name: 'Kroger', color: '#0C3E80' },
    { id: 'walmart', name: 'Walmart', color: '#0071DC' },
    { id: 'target', name: 'Target', color: '#CC0000' },
    { id: 'cvs', name: 'CVS', color: '#CC0000' },
    { id: 'walgreens', name: 'Walgreens', color: '#E31837' },
    { id: 'costco', name: 'Costco', color: '#E31837' },
    { id: 'publix', name: 'Publix', color: '#3B8C3B' },
    { id: 'aldi', name: 'ALDI', color: '#00529B' },
  ];
  
  // Generate bundle when stores change - USE SUPABASE
  useEffect(() => {
    const createBundle = async () => {
      const stores = selectedStores || [];
      if (stores.length > 0) {
        setLoading(true);
        try {
          const newBundle = await createSupabaseBundle(stores, "Guest");
          setBundle(newBundle);
          saveToLocalStorage(STORAGE_KEYS.SELECTED_STORES, stores);
          saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, newBundle);
        } catch (e) {
          console.error("Failed to create bundle:", e);
          setBundle(null);
        } finally {
          setLoading(false);
        }
      } else {
        setBundle(null);
      }
    };
    createBundle();
  }, [selectedStores]);
  
  const toggleStore = (storeId) => {
    setSelectedStores(prev => {
      const current = prev || [];
      return current.includes(storeId)
        ? current.filter(id => id !== storeId)
        : [...current, storeId];
    });
  };
  
  const handleRefreshBundle = async () => {
    const stores = selectedStores || [];
    if (stores.length > 0) {
      setLoading(true);
      try {
        const newBundle = await createSupabaseBundle(stores, "Guest");
        setBundle(newBundle);
        saveToLocalStorage(STORAGE_KEYS.CURRENT_BUNDLE, newBundle);
      } catch (e) {
        console.error("Failed to refresh bundle:", e);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const getStoreName = (storeId) => {
    const store = availableStores.find(s => s.id === storeId);
    return store ? store.name : storeId;
  };
  
  // Safe accessors for bundle properties
  const bundleWeekLabel = bundle?.week_label || bundle?.weekLabel || "This Week's Savings";
  const bundleCouponCount = bundle?.coupon_count || bundle?.couponCount || 0;
  const bundleTotalSavings = bundle?.total_savings || bundle?.totalSavings || 0;
  const bundleValidUntil = bundle?.valid_until || bundle?.validUntil || "N/A";
  const bundleStores = bundle?.stores_included || bundle?.stores || [];
  const bundleCoupons = bundle?.coupons || [];
  
  const storesArray = selectedStores || [];
  
  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="welcome-section">
            <h1 data-testid="dashboard-title">This Week's Savings</h1>
            <p className="tier-badge">Ready for checkout</p>
          </div>
        </div>
        
        {/* Store Selection Bar */}
        <div className="store-selection-bar">
          <div className="selected-stores-display">
            {storesArray.map(storeId => (
              <span key={storeId} className="store-chip-small">
                {getStoreName(storeId)}
              </span>
            ))}
            <button 
              className="btn-text-small" 
              onClick={() => setShowStoreSelector(!showStoreSelector)}
            >
              {showStoreSelector ? 'Done' : 'Change Stores'}
            </button>
          </div>
        </div>
        
        {/* Store Selector (collapsible) */}
        {showStoreSelector && (
          <div className="store-selector-inline">
            {availableStores.map(store => (
              <button
                key={store.id}
                className={`store-btn ${storesArray.includes(store.id) ? 'selected' : ''}`}
                onClick={() => toggleStore(store.id)}
                style={{ borderColor: storesArray.includes(store.id) ? store.color : undefined }}
              >
                {store.name}
                {storesArray.includes(store.id) && <span className="check-mark">✓</span>}
              </button>
            ))}
          </div>
        )}
        
        {/* Loading State */}
        {loading && (
          <div className="loading-state" style={{ padding: '40px', textAlign: 'center' }}>
            <div className="spinner-small" style={{ margin: '0 auto 16px' }}></div>
            <p>Creating your bundle...</p>
          </div>
        )}
        
        {/* Bundle Display */}
        {!loading && bundle && (
          <div className="master-bundle-card" data-testid="master-bundle">
            {/* Bundle Header */}
            <div className="bundle-header">
              <div className="bundle-week">
                <span className="week-label">{bundleWeekLabel}</span>
                <span className="bundle-type-badge">
                  {bundleCouponCount} coupons from {bundleStores.length || storesArray.length} store{(bundleStores.length || storesArray.length) > 1 ? 's' : ''}
                </span>
              </div>
              <div className="bundle-expiry">
                Valid until {typeof bundleValidUntil === 'string' && bundleValidUntil.includes('T') 
                  ? new Date(bundleValidUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : bundleValidUntil}
              </div>
            </div>
            
            {/* Stats */}
            <div className="bundle-stats">
              <div className="stat-item">
                <span className="stat-number">{bundleCouponCount}</span>
                <span className="stat-text">Coupons</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <span className="stat-number">${typeof bundleTotalSavings === 'number' ? bundleTotalSavings.toFixed(2) : bundleTotalSavings}</span>
                <span className="stat-text">Est. Savings</span>
              </div>
            </div>
            
            {/* Coupon List - Always visible, clean and simple */}
            <div className="coupon-list-section" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, color: '#1F2A44' }}>Your Coupons</h4>
                <button className="btn-text-small" onClick={() => setShowCoupons(!showCoupons)}>
                  {showCoupons ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showCoupons && bundleCoupons.length > 0 && (
                <div className="coupon-list">
                  {bundleCoupons.map((coupon, index) => (
                    <div key={coupon.id || index} className="coupon-item">
                      <div className="coupon-info">
                        <span className="coupon-store">{coupon.store_name || getStoreName(coupon.store_id || coupon.storeId)}</span>
                        <span className="coupon-title">{coupon.title}</span>
                        <span className="coupon-desc">{coupon.description}</span>
                      </div>
                      <div className="coupon-savings">{coupon.discount}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {showCoupons && bundleCoupons.length === 0 && (
                <p style={{ color: '#718096', textAlign: 'center', padding: '20px' }}>
                  No coupons available for selected stores yet.
                </p>
              )}
            </div>
            
            {/* QR Code Section */}
            <div className="qr-section" style={{ marginTop: '24px' }}>
              <div className="qr-wrapper">
                <QRCodeDisplay bundleId={bundle.id} size={200} />
              </div>
              <p className="qr-instruction">Scan for quick access</p>
              <p style={{ fontSize: '0.8rem', color: '#718096', marginTop: '8px' }}>
                (Backup method - show coupons above at checkout)
              </p>
            </div>
            
            {/* Refresh Button */}
            <button 
              className="btn-secondary" 
              onClick={handleRefreshBundle}
              disabled={loading}
              style={{ width: '100%', marginTop: '16px' }}
            >
              {loading ? '↻ Refreshing...' : '↻ Refresh Bundle'}
            </button>
          </div>
        )}
        
        {!loading && !bundle && storesArray.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🛒</div>
            <h3>Select Your Stores</h3>
            <p>Choose at least one store to see your savings bundle.</p>
            <button className="btn-primary" onClick={() => setShowStoreSelector(true)}>
              Select Stores
            </button>
          </div>
        )}
        
        {!loading && !bundle && storesArray.length > 0 && (
          <div className="empty-state">
            <div className="empty-icon">⏳</div>
            <h3>Loading Bundle...</h3>
            <p>Please wait while we fetch your coupons.</p>
          </div>
        )}
        )}
      </div>
    </div>
  );
};

// ============== MAIN APP ==============

function App() {
  const [user, setUser] = useState(null);
  const [useQuickMode, setUseQuickMode] = useState(true); // Default to quick mode for zero friction
  
  useEffect(() => {
    // Check if user prefers quick mode (no login)
    const quickMode = loadFromLocalStorage('scansavvy_quick_mode', true);
    setUseQuickMode(quickMode);
    
    // Only try to load user if not in quick mode
    if (!quickMode) {
      const savedUserId = localStorage.getItem('scansavvy_user_id');
      if (savedUserId) {
        axios.get(`${API}/users/${savedUserId}`)
          .then(res => setUser(res.data))
          .catch(() => localStorage.removeItem('scansavvy_user_id'));
      }
    }
  }, []);
  
  const handleLogin = (userData) => {
    setUser(userData);
    setUseQuickMode(false);
    localStorage.setItem('scansavvy_user_id', userData.id);
    saveToLocalStorage('scansavvy_quick_mode', false);
  };
  
  const handleLogout = () => {
    setUser(null);
    setUseQuickMode(true);
    localStorage.removeItem('scansavvy_user_id');
    saveToLocalStorage('scansavvy_quick_mode', true);
    window.location.href = '/';
  };
  
  const handleUpdateUser = (userData) => {
    setUser(userData);
  };
  
  return (
    <div className="App">
      <BrowserRouter>
        <Header user={user} onLogout={handleLogout} />
        <Routes>
          <Route path="/" element={
            <HomePage onGetStarted={() => window.location.href = '/bundle'} />
          } />
          <Route path="/bundle" element={<QuickBundlePage />} />
          <Route path="/onboarding" element={
            <OnboardingPage onComplete={(userData) => {
              handleLogin(userData);
              window.location.href = '/dashboard';
            }} />
          } />
          <Route path="/dashboard" element={
            user ? (
              <DashboardPage user={user} onUpdateUser={handleUpdateUser} />
            ) : (
              <QuickBundlePage />
            )
          } />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
